import { Txn, Category, Group, Budget } from '../money.model';
import { spendableTxns, monthKey, recentMonths } from '../dashboard/dashboard.aggregate';
import { median } from '../rules/recurring.detector';

/**
 * Convert cents to a draft string for the input without losing cents.
 * A whole-dollar budget (e.g., 75000) seeds as a bare integer ("750").
 * A fractional-dollar budget (e.g., 1250) preserves the cent ("12.5").
 */
export function centsToDraft(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? String(Math.floor(dollars)) : String(dollars);
}

/**
 * Decide whether the draft input represents a change from the stored budget.
 * Treats a cleared input with no stored budget as unchanged.
 * Treats a stored 0 as a real value, not as absence.
 */
export function budgetChanged(nextCents: number | null, storedCents: number | undefined): boolean {
  // Cleared input (null) with no stored budget (undefined) = no change.
  if (nextCents === null && storedCents === undefined) {
    return false;
  }
  // Any other mismatch is a change. This correctly handles stored 0 as a real value.
  return nextCents !== storedCents;
}

export interface CategoryGroupRow { group: Group; categories: Category[]; }

/**
 * Categories grouped under their group heading, group order then category
 * order, so the flat "By category" list stops interleaving groups (Gas,
 * Gifts, Groceries, Medical, Paycheck, Restaurants, ...) and matches the
 * grouped "By group" rollup directly above it and the Categories screen's
 * own nesting.
 *
 * Non-spending groups (income) are omitted entirely: spentCents on an income
 * category is structurally always 0 (see budgetRows), so a budget input
 * there can never do anything. A category whose group no longer exists is
 * silently dropped, the same way it would be from a grouped rollup.
 *
 * Kept local to budgets.calc.ts rather than reusing categories.component's
 * groupCategories: that helper also threads Rule[] through for a per-category
 * rule list the budgets screen has no use for and does not load, and it does
 * not filter by isSpending. Pulling it in would mean loading rules just to
 * satisfy a signature, for a screen that has no other reason to know about
 * them.
 */
export function groupCategoriesForBudget(groups: Group[], categories: Category[]): CategoryGroupRow[] {
  return [...groups]
    .filter(g => g.isSpending)
    .sort((a, b) => a.order - b.order)
    .map(group => ({
      group,
      categories: categories
        .filter(c => c.groupId === group.id)
        .sort((a, b) => a.order - b.order),
    }));
}

export interface BudgetRow {
  categoryId: string;
  categoryName: string;
  spentCents: number;
  limitCents: number;
  remainingCents: number;
  /** 0–100, clamped for bar width; `isOver` carries the overspend signal. */
  pctUsed: number;
  isOver: boolean;
}

export interface GroupBudgetRow {
  groupId: string;
  groupName: string;
  color: string;
  spentCents: number;
  limitCents: number;
  pctUsed: number;
  isOver: boolean;
}

/**
 * The limit in force for a month. A month override of zero is a deliberate
 * "spend nothing here" and must not fall back to the default.
 */
export function budgetForMonth(budget: Budget | undefined, month: string): number | null {
  if (!budget) return null;
  const override = budget.overrides ? budget.overrides[month] : undefined;
  if (override !== undefined && override !== null) return override;
  // An overrides-only node (setBudget with a month but no prior default) has
  // no `amount` field at all, even though the type promises `number` — so
  // `budget.amount` can genuinely be undefined at runtime. `??`, not falling
  // through raw, keeps that case a real null instead of "$NaN" at render.
  return budget.amount ?? null;
}

/**
 * True when the displayed month has an override in force, as distinct from
 * falling back to the default. Drives the "override" badge — a row using
 * an override must say so rather than silently showing a number that looks
 * like the default.
 */
export function hasOverrideForMonth(budget: Budget | undefined, month: string): boolean {
  const override = budget?.overrides ? budget.overrides[month] : undefined;
  return override !== undefined && override !== null;
}

const pct = (spent: number, limit: number): number => {
  if (limit > 0) return Math.min(100, (spent / limit) * 100);
  return spent > 0 ? 100 : 0;      // never Infinity from a zero limit
};

/**
 * One row per budgeted category, most-consumed first.
 *
 * Spend nets inflows against outflows within the category, so a refund reduces
 * what the budget has consumed rather than being ignored.
 */
export function budgetRows(
  txns: Txn[], month: string, categories: Category[], budgets: Record<string, Budget>,
): BudgetRow[] {
  const inMonth = spendableTxns(txns).filter(t => monthKey(t.date) === month);
  const catById = new Map(categories.map(c => [c.id, c]));

  const rows: BudgetRow[] = [];
  Object.keys(budgets ?? {}).forEach(categoryId => {
    const limitCents = budgetForMonth(budgets[categoryId], month);
    if (limitCents === null) return;

    const net = inMonth
      .filter(t => t.categoryId === categoryId)
      .reduce((n, t) => n - t.amount, 0);        // outflow is negative, so subtract
    const spentCents = Math.max(0, net);

    rows.push({
      categoryId,
      categoryName: catById.get(categoryId)?.name ?? 'Unknown',
      spentCents,
      limitCents,
      remainingCents: limitCents - spentCents,
      pctUsed: pct(spentCents, limitCents),
      isOver: spentCents > limitCents,
    });
  });

  return rows.sort((a, b) => b.pctUsed - a.pctUsed || b.spentCents - a.spentCents);
}

/** Category rows summed to their groups, in group order. */
export function groupBudgetRollup(
  rows: BudgetRow[], categories: Category[], groups: Group[],
): GroupBudgetRow[] {
  const catById = new Map(categories.map(c => [c.id, c]));
  const totals = new Map<string, { spent: number; limit: number }>();

  rows.forEach(r => {
    const groupId = catById.get(r.categoryId)?.groupId;
    if (!groupId) return;
    const acc = totals.get(groupId) ?? { spent: 0, limit: 0 };
    acc.spent += r.spentCents;
    acc.limit += r.limitCents;
    totals.set(groupId, acc);
  });

  return [...groups]
    .sort((a, b) => a.order - b.order)
    .filter(g => totals.has(g.id))
    .map(g => {
      const acc = totals.get(g.id)!;
      return {
        groupId: g.id, groupName: g.name, color: g.color,
        spentCents: acc.spent, limitCents: acc.limit,
        pctUsed: pct(acc.spent, acc.limit),
        isOver: acc.spent > acc.limit,
      };
    });
}

/** Same trailing window the savings chips use — completed months only. */
const SUGGEST_WINDOW_MONTHS = 6;

/**
 * A draft budget per spending category: the MEDIAN of its monthly spend over
 * the trailing six completed months, zero months included. Median, not mean —
 * one renovation-sized month must not inflate a suggestion — and zeros count
 * because a month a bimonthly bill skips genuinely cost $0: gas at $70 every
 * other month suggests $35/mo, which is its true monthly cost. A category
 * whose median is zero gets no suggestion (not regular enough to budget), and
 * income categories are never suggested.
 */
export function suggestBudgets(
  txns: Txn[], categories: Category[], groups: Group[], asOf: string,
): Record<string, number> {
  const lastCompleted = recentMonths(asOf.slice(0, 7), 2)[0];
  const window = recentMonths(lastCompleted, SUGGEST_WINDOW_MONTHS);
  const windowSet = new Set(window);

  const spendingCats = new Set(
    categories.filter(c => groups.find(g => g.id === c.groupId)?.isSpending).map(c => c.id));

  const perCatMonth = new Map<string, Map<string, number>>();
  spendableTxns(txns ?? []).forEach(t => {
    if (!t.date || t.amount >= 0 || !t.categoryId || !spendingCats.has(t.categoryId)) return;
    const m = monthKey(t.date);
    if (!windowSet.has(m)) return;
    const inner = perCatMonth.get(t.categoryId) ?? new Map<string, number>();
    inner.set(m, (inner.get(m) ?? 0) + -t.amount);
    perCatMonth.set(t.categoryId, inner);
  });

  const out: Record<string, number> = {};
  perCatMonth.forEach((byMonth, catId) => {
    const values = window.map(m => byMonth.get(m) ?? 0);
    const med = median(values);
    if (med > 0) out[catId] = med;
  });
  return out;
}
